import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { useSelector } from 'react-redux';
import {
  selectCSVWoburnReassignmentReportData,
  selectEmployeeData,
  selectWoburnReassignmentReportData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  getCSVWoburnReassignmentReport,
  getEmployeeList,
  getWoburnReassignmentReportData,
  setCSVWoburnReassignmentReport,
} from '../../../store/ReportsDetailsSlice/actions';
import moment from 'moment';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import ReusableGrid from '../../../components/Grid';
import useWoburnReassignmentReportColumns from './hooks/useWoburnReassignmentReportColumns';
import useWoburnReassignmentReportRows from './hooks/useWoburnReassignmentReportRows';
import { useForm } from 'react-hook-form';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { GROUPBY } from '../../../constants/Reports';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';

const defaultFormValues = {
  emp_code: { id: 'ALL', name: 'All' },
  groupby: { id: GROUPBY[0].value, name: GROUPBY[0].title },
};

const WoburnReassignmentReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { woburnReassignmentReportData, isLoadingWoburnReassignmentReportData } = useSelector(
    selectWoburnReassignmentReportData
  );
	const { CSVWoburnReassignmentReport, isLoadingCSVWoburnReassignmentReport } = useSelector(
    selectCSVWoburnReassignmentReportData
  );
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });

  const { fromDate, toDate, render } = useDateRangeFilter();
  const { emp_code: employees, groupby } = watch();

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employees.id !== 'ALL' ? employees.id : 'ALL',
    };
  }, [fromDate, toDate, employees, groupby.id]);

  useEffect(
    () => {
      dispatch(getEmployeeList());
      dispatch(getWoburnReassignmentReportData(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (CSVWoburnReassignmentReport.data) {
      saveCsvToFile(
        CSVWoburnReassignmentReport.data,
        CSVWoburnReassignmentReport.filename
      );
      dispatch(setCSVWoburnReassignmentReport({ data: null, filename: '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CSVWoburnReassignmentReport]);

  const handleFindClick = () =>
    dispatch(getWoburnReassignmentReportData(param));

  const handleDownloadReport = () => {
    dispatch(getCSVWoburnReassignmentReport({ ...param, csv: 1 }));
  };

  const columns = useWoburnReassignmentReportColumns(
    woburnReassignmentReportData.headers
  );
  const rows = useWoburnReassignmentReportRows(woburnReassignmentReportData);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
          enableAllOption={true}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
		{
			key: 'export',
			label: '',
			show: true,
			renderComponent: () => (
				<Grid
					item
					sx={{
						width: '100%',
						display: 'flex',
						gap: 2,
						justifyContent: isTabletVerticalWidth ? 'start' : 'end',
						alignItems: 'center',
						height: '100%',
						paddingTop: '23px',
					}}
				>
					<ExportCsvButton
						onClick={handleDownloadReport}
						disabled={isLoadingCSVWoburnReassignmentReport}
						text={'Export CSV'}
					/>
				</Grid>
			),
		},
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn Reassignment Report Details" />

      <Box container xs={12} className="d-flex justify-content-between">
        <Box sx={{ width: '100%', mr: 2 }}>
          <Box container spacing={2}>
            <Grid container sx={{ width: '100%', mb: '24px' }}>
              <FilterPanel filters={filterConfig} columns={isTabletVerticalWidth ? 1 : rawFilterConfig.length} />
            </Grid>
            <Grid container sx={{ width: '100%', mb: '24px' }} spacing={2}>
              {render}
            </Grid>
          </Box>
        </Box>
      </Box>

      {isLoadingWoburnReassignmentReportData ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {!!rows.length ? (
            <Box mt={4}>
              {rows.map((item) => (
                <Box key={item.name} mb={4}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {item.name}
                  </Typography>
                  <ReusableGrid columns={columns} rows={item.items} />
                </Box>
              ))}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
    </Paper>
  );
};

export default WoburnReassignmentReport;
